<template>
  <div class="chat-scrape-person">
    <button
      v-for="person in visiblePersonCandidates"
      :key="person.candidate_id"
      type="button"
      class="chat-scrape-person__option"
      :class="{
        'chat-scrape-person__option--selected':
          selectedPersonId === person.candidate_id && !manualName.trim(),
      }"
      :disabled="busy"
      @click="selectCandidate(person.candidate_id)"
    >
      <span class="text-body-2 font-weight-medium">
        {{ personDisplayName(person) }}
      </span>
      <span
        v-if="person.scraped_role"
        class="text-caption text-medium-emphasis"
      >
        {{ person.scraped_role }}
      </span>
    </button>

    <v-text-field
      v-model="manualName"
      variant="outlined"
      density="compact"
      rounded="lg"
      hide-details
      class="mt-1"
      :disabled="busy"
      :placeholder="t('onboarding.wizard.chat-widgets.scrape-review-manual-placeholder')"
      @focus="selectedPersonId = null"
    />

    <v-btn
      color="primary"
      rounded="lg"
      block
      class="mt-3"
      :disabled="busy || !canConfirm"
      :loading="busy"
      @click="confirm"
    >
      {{ t('onboarding.wizard.chat-widgets.scrape-review-confirm') }}
    </v-btn>

    <button
      type="button"
      class="chat-scrape-person__skip text-caption font-weight-bold text-primary"
      :disabled="busy"
      @click="skip"
    >
      {{ t('onboarding.wizard.chat-widgets.scrape-review-skip') }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

export interface ScrapeReviewPerson {
  candidate_id: string
  first_name?: string
  last_name?: string
  display_name?: string
  scraped_role?: string | null
}

const props = defineProps<{
  personCandidates: ScrapeReviewPerson[]
  busy?: boolean
}>()

const emit = defineEmits<{
  confirm: [payload: { candidate_id?: string | null; first_name?: string; last_name?: string }]
}>()

const { t } = useI18n()
const selectedPersonId = ref<string | null>(null)
const manualName = ref('')

const canConfirm = computed(() => Boolean(manualName.value.trim() || selectedPersonId.value))

// Placeholder tokens the scraper occasionally emits for a missing name — mirror
// the backend guard so a bogus "null null" candidate never renders here either.
const NAME_STOPWORDS = new Set(['null', 'none', 'nil', 'na', 'n/a', 'nan', 'undefined', 'unknown'])

const isRealNamePart = (value?: string | null): boolean => {
  const parts = String(value || '')
    .toLowerCase()
    .split(/\s+/)
    .map((t) => t.replace(/^[.,-]+|[.,-]+$/g, ''))
    .filter(Boolean)
  return parts.length > 0 && parts.some((t) => !NAME_STOPWORDS.has(t))
}

const personDisplayName = (person: ScrapeReviewPerson): string => {
  const first = isRealNamePart(person.first_name) ? String(person.first_name).trim() : ''
  const last = isRealNamePart(person.last_name) ? String(person.last_name).trim() : ''
  if (first || last) return [first, last].filter(Boolean).join(' ')
  const display = isRealNamePart(person.display_name) ? String(person.display_name).trim() : ''
  return display || '—'
}

const visiblePersonCandidates = computed(() =>
  props.personCandidates.filter(
    (p) =>
      isRealNamePart(p.first_name) || isRealNamePart(p.last_name) || isRealNamePart(p.display_name)
  )
)

const selectCandidate = (candidateId: string) => {
  selectedPersonId.value = candidateId
  manualName.value = ''
}

const confirm = () => {
  // Manual entry wins: split the typed full name into first / last.
  const typed = manualName.value.trim()
  if (typed) {
    const [first, ...rest] = typed.split(/\s+/)
    emit('confirm', { first_name: first, last_name: rest.join(' ') })
    return
  }
  emit('confirm', { candidate_id: selectedPersonId.value })
}

const skip = () => {
  emit('confirm', { candidate_id: null })
}
</script>

<style scoped>
.chat-scrape-person {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.chat-scrape-person__option {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
  padding: 10px 14px;
  border: 2px solid rgba(var(--v-border-color), 0.25);
  border-radius: 999px;
  background: #fff;
  cursor: pointer;
  text-align: left;
}

.chat-scrape-person__option--selected {
  border-color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.06);
}

.chat-scrape-person__skip {
  align-self: center;
  margin-top: 4px;
  background: none;
  border: none;
  cursor: pointer;
  letter-spacing: 0.04em;
}
</style>
